import React, { useState } from 'react';
import { RELATIONSHIPS, PROFESSIONS } from './constants';
import moment from 'moment'; // Assuming you're using moment for date manipulation
import { DatePicker } from 'components/inputs';
import { formatDate } from 'lib/formatters/date';
import { upperCase } from 'lodash';

const MemberFormModal = ({ showModal, toggleModal, newMember, handleChange, saveMember, isEditing, hasHouseholdHead, handleDateChange, errors }) => {
  const [validationErrors, setValidationErrors] = useState({});

  if (!showModal) return null;

  // Validate full name: exactly 3 words, each at least 3 letters, only English alphabet
  const validateFullName = (fullName) => {
    if (!fullName.trim()) {
      return 'Full Name is required';
    }

    // Remove extra spaces and split by single space
    const trimmedName = fullName.trim().replace(/\s+/g, ' ');
    const words = trimmedName.split(' ');

    // Check if exactly 3 words
    if (words.length !== 3) {
      return 'Full Name must be exactly 3 words (First Middle Last)';
    }

    // Check each word
    for (let i = 0; i < words.length; i++) {
      const word = words[i];
      
      // Check if word is at least 3 letters
      if (word.length < 3) {
        return `Each word must be at least 3 letters long. "${word}" has only ${word.length} letter(s)`;
      }

      // Check if word contains only English alphabet (a-zA-Z)
      if (!/^[a-zA-Z]+$/.test(word)) {
        return `Only English alphabet letters (a-z, A-Z) are allowed. "${word}" contains invalid characters`;
      }
    }

    return null; // Valid
  };

  // Validation function to check for required fields
  const validateForm = () => {
    const errors = {};

    // Full Name Validation using enhanced validation
    const fullNameValidationError = validateFullName(newMember.fullName);
    if (fullNameValidationError) {
      errors.fullName = fullNameValidationError;
    }

    if (!newMember.birthdate) errors.birthdate = 'Date of Birth is required';
    if (!newMember.gender) errors.gender = 'Gender is required';
    if (!newMember.localMembershipNumber.trim()) errors.localMembershipNumber = 'Local Membership Number is required';

    // Required for the head of household only (as the backend); anyone else may leave it
    // blank, but a value given must be exactly 16 digits.
    const nationalId = (newMember.nationalId || '').trim();
    if (!nationalId) {
      if (newMember.relationshipToHead === 'SELF') errors.nationalId = 'National ID is required for the head of household';
    } else if (!/^\d{16}$/.test(nationalId)) {
      errors.nationalId = 'National ID must be exactly 16 digits';
    }

    if (!newMember.relationshipToHead) errors.relationshipToHead = 'Relationship to Head is required';
    if (!newMember.profession) errors.profession = 'Profession is required';
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle change for full name - filter input but don't show errors until button click
  const handleFullNameChange = (e) => {
    const { value } = e.target;
    // Filter input to only allow English alphabet and spaces
    const filteredValue = value.replace(/[^a-zA-Z\s]/g, '');
    // Create a synthetic event with filtered value
    const syntheticEvent = {
      ...e,
      target: {
        ...e.target,
        name: 'fullName',
        value: filteredValue
      }
    };
    handleChange(syntheticEvent);
    // Clear validation error when user types (errors only show on button click)
    if (validationErrors.fullName) {
      const { fullName, ...restErrors } = validationErrors;
      setValidationErrors(restErrors);
    }
  };

  const handleSaveMember = () => {
    if (validateForm()) {
      saveMember(newMember);
    }
  };

  return (
    <div className="tw-fixed tw-inset-0 tw-bg-gray-900 tw-bg-opacity-60 tw-flex tw-justify-center tw-items-center tw-z-50 tw-p-4">
      <div className="tw-w-full tw-max-w-md tw-bg-white tw-rounded-xl tw-shadow-2xl tw-border tw-border-gray-200 tw-overflow-hidden tw-max-h-[90vh] tw-flex tw-flex-col">
        {/* Sticky Header */}
        <div className="tw-bg-gradient-to-r tw-from-blue-500 tw-to-indigo-600 tw-px-6 tw-py-4 tw-flex tw-items-center tw-justify-between tw-sticky tw-top-0 tw-z-10 tw-shadow-md">
          <div className="tw-flex tw-items-center">
            <div className="tw-bg-white tw-bg-opacity-20 tw-p-2 tw-rounded-lg tw-mr-3">
              <svg className="tw-w-5 tw-h-5 tw-text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <h3 className="tw-text-xl tw-font-bold tw-text-white">{isEditing ? 'Edit Member' : 'Add Member'}</h3>
          </div>
          <button
            onClick={toggleModal}
            className="tw-text-white tw-hover:bg-white tw-hover:bg-opacity-20 tw-rounded-full tw-p-1 tw-transition-colors tw-duration-200"
            aria-label="Close modal"
          >
            <svg className="tw-w-5 tw-h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="tw-flex-1 tw-overflow-y-auto">
          <form className="tw-p-6">
          <div className="tw-mb-5">
            <label className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-2 tw-flex tw-items-center">
              <svg className="tw-w-4 tw-h-4 tw-mr-1.5 tw-text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Full Name
            </label>
            <input
              type="text"
              name="fullName"
              value={newMember.fullName}
              onChange={handleFullNameChange}
              className={`tw-w-full tw-p-3 tw-border tw-rounded-lg tw-shadow-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-blue-500 tw-transition-all tw-duration-200 ${
                validationErrors.fullName 
                  ? 'tw-border-red-500 tw-bg-red-50' 
                  : 'tw-border-gray-300 hover:tw-border-gray-400'
              }`}
              placeholder="First Middle Last (3 words, each at least 3 letters)"
              required
            />
            {validationErrors.fullName && (
              <p className="tw-text-red-600 tw-text-sm tw-mt-1.5 tw-flex tw-items-center">
                <svg className="tw-w-4 tw-h-4 tw-mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {validationErrors.fullName}
              </p>
            )}
          </div>

          {/* DatePicker for birthdate */}
          <div className="tw-mb-5">
            <label className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-2 tw-flex tw-items-center">
              <svg className="tw-w-4 tw-h-4 tw-mr-1.5 tw-text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Date of Birth
            </label>
            <DatePicker
              label="Date of Birth"
              onChange={(gregorianDate, ethiopianDate) => handleDateChange(gregorianDate, 'birthdate')}
              defaultDate={formatDate(newMember.birthdate)}
              maxDate={formatDate(moment())}
              name="birthdate"
              overlayCalendar={true}
              error={validationErrors.birthdate}
            />
            {validationErrors.birthdate && (
              <p className="tw-text-red-600 tw-text-sm tw-mt-1.5 tw-flex tw-items-center">
                <svg className="tw-w-4 tw-h-4 tw-mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {validationErrors.birthdate}
              </p>
            )}
          </div>

          <div className="tw-mb-5">
            <label className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-2 tw-flex tw-items-center">
              <svg className="tw-w-4 tw-h-4 tw-mr-1.5 tw-text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              Gender
            </label>
            <select
              name="gender"
              value={newMember.gender}
              onChange={handleChange}
              className={`tw-w-full tw-p-3 tw-border tw-rounded-lg tw-shadow-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-blue-500 tw-transition-all tw-duration-200 tw-bg-white ${
                validationErrors.gender 
                  ? 'tw-border-red-500 tw-bg-red-50' 
                  : 'tw-border-gray-300 hover:tw-border-gray-400'
              }`}
              required
            >
              <option value="">Select Gender</option>
              <option value="M">Male</option>
              <option value="F">Female</option>
            </select>
            {validationErrors.gender && (
              <p className="tw-text-red-600 tw-text-sm tw-mt-1.5 tw-flex tw-items-center">
                <svg className="tw-w-4 tw-h-4 tw-mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {validationErrors.gender}
              </p>
            )}
          </div>

          <div className="tw-mb-5">
            <label className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-2 tw-flex tw-items-center">
              <svg className="tw-w-4 tw-h-4 tw-mr-1.5 tw-text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
              </svg>
              Local Membership Number
            </label>
            <input
              type="text"
              name="localMembershipNumber"
              value={newMember.localMembershipNumber}
              onChange={handleChange}
              className={`tw-w-full tw-p-3 tw-border tw-rounded-lg tw-shadow-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-blue-500 tw-transition-all tw-duration-200 ${
                validationErrors.localMembershipNumber 
                  ? 'tw-border-red-500 tw-bg-red-50' 
                  : 'tw-border-gray-300 hover:tw-border-gray-400'
              }`}
              required
            />
            {validationErrors.localMembershipNumber && (
              <p className="tw-text-red-600 tw-text-sm tw-mt-1.5 tw-flex tw-items-center">
                <svg className="tw-w-4 tw-h-4 tw-mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {validationErrors.localMembershipNumber}
              </p>
            )}
          </div>

          <div className="tw-mb-5">
            <label className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-2 tw-flex tw-items-center">
              <svg className="tw-w-4 tw-h-4 tw-mr-1.5 tw-text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
              </svg>
              National ID
            </label>
            <input
              type="text"
              name="nationalId"
              value={newMember.nationalId || ''}
              onChange={handleChange}
              inputMode="numeric"
              maxLength={16}
              placeholder="16 digits"
              className={`tw-w-full tw-p-3 tw-border tw-rounded-lg tw-shadow-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-blue-500 tw-transition-all tw-duration-200 ${
                validationErrors.nationalId
                  ? 'tw-border-red-500 tw-bg-red-50'
                  : 'tw-border-gray-300 hover:tw-border-gray-400'
              }`}
              required
            />
            {validationErrors.nationalId && (
              <p className="tw-text-red-600 tw-text-sm tw-mt-1.5 tw-flex tw-items-center">
                <svg className="tw-w-4 tw-h-4 tw-mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {validationErrors.nationalId}
              </p>
            )}
          </div>

          <div className="tw-mb-5">
            <label className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-2 tw-flex tw-items-center">
              <svg className="tw-w-4 tw-h-4 tw-mr-1.5 tw-text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              Relationship to Head
            </label>
            <select
              name="relationshipToHead"
              value={newMember.relationshipToHead}
              onChange={handleChange}
              className={`tw-w-full tw-p-3 tw-border tw-rounded-lg tw-shadow-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-blue-500 tw-transition-all tw-duration-200 tw-bg-white ${
                validationErrors.relationshipToHead 
                  ? 'tw-border-red-500 tw-bg-red-50' 
                  : 'tw-border-gray-300 hover:tw-border-gray-400'
              }`}
              required
            >
              <option value="">Select Relationship</option>
              {Object.values(RELATIONSHIPS).map((relationship, index) => (
                <option key={index} value={upperCase(relationship)}>
                  {relationship}
                </option>
              ))}
            </select>
            {validationErrors.relationshipToHead && (
              <p className="tw-text-red-600 tw-text-sm tw-mt-1.5 tw-flex tw-items-center">
                <svg className="tw-w-4 tw-h-4 tw-mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {validationErrors.relationshipToHead}
              </p>
            )}
          </div>

          <div className="tw-mb-5">
            <label className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-2 tw-flex tw-items-center">
              <svg className="tw-w-4 tw-h-4 tw-mr-1.5 tw-text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Profession
            </label>
            <select
              name="profession"
              value={newMember.profession}
              onChange={handleChange}
              className={`tw-w-full tw-p-3 tw-border tw-rounded-lg tw-shadow-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-blue-500 tw-transition-all tw-duration-200 tw-bg-white ${
                validationErrors.profession 
                  ? 'tw-border-red-500 tw-bg-red-50' 
                  : 'tw-border-gray-300 hover:tw-border-gray-400'
              }`}
              required
            >
              <option value="">Select Profession</option>
              {Object.values(PROFESSIONS).map((profession, index) => (
                <option key={index} value={upperCase(profession)}>
                  {profession}
                </option>
              ))}
            </select>
            {validationErrors.profession && (
              <p className="tw-text-red-600 tw-text-sm tw-mt-1.5 tw-flex tw-items-center">
                <svg className="tw-w-4 tw-h-4 tw-mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {validationErrors.profession}
              </p>
            )}
          </div>

          <div className="tw-mb-6">
            <label className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-2 tw-flex tw-items-center">
              <svg className="tw-w-4 tw-h-4 tw-mr-1.5 tw-text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Upload Photo (Optional)
            </label>
            <div className="tw-mt-1 tw-flex tw-justify-center tw-px-6 tw-pt-5 tw-pb-6 tw-border-2 tw-border-gray-300 tw-border-dashed tw-rounded-lg tw-hover:border-blue-400 tw-transition-colors tw-duration-200">
              <div className="tw-space-y-1 tw-text-center">
                <svg className="tw-mx-auto tw-h-12 tw-w-12 tw-text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                  <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <div className="tw-flex tw-text-sm tw-text-gray-600">
                  <label htmlFor="photo-upload" className="tw-relative tw-cursor-pointer tw-bg-white tw-rounded-md tw-font-medium tw-text-blue-600 hover:tw-text-blue-500 focus-within:tw-outline-none focus-within:tw-ring-2 focus-within:tw-ring-offset-2 focus-within:tw-ring-blue-500">
                    <span>Upload a file</span>
                    <input
                      id="photo-upload"
                      name="photo"
                      type="file"
                      onChange={handleChange}
                      className="tw-sr-only"
                    />
                  </label>
                  <p className="tw-pl-1">or drag and drop</p>
                </div>
                <p className="tw-text-xs tw-text-gray-500">PNG, JPG, GIF up to 10MB</p>
              </div>
            </div>
          </div>
          </form>
        </div>

        {/* Sticky Footer with Actions */}
        <div className="tw-sticky tw-bottom-0 tw-z-10 tw-bg-white tw-border-t tw-border-gray-200 tw-px-6 tw-py-4 tw-shadow-lg">
          <div className="tw-flex tw-justify-end tw-space-x-3">
            <button
              type="button"
              onClick={toggleModal}
              className="tw-bg-gray-100 tw-text-gray-700 tw-px-5 tw-py-2.5 tw-rounded-lg tw-font-semibold hover:tw-bg-gray-200 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-gray-400 focus:tw-ring-offset-2 tw-transition-all tw-duration-200 tw-flex tw-items-center"
            >
              <svg className="tw-w-4 tw-h-4 tw-mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveMember}
              className="tw-bg-gradient-to-r tw-from-green-500 tw-to-emerald-500 tw-text-white tw-px-5 tw-py-2.5 tw-rounded-lg tw-font-semibold hover:tw-from-green-600 hover:tw-to-emerald-600 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-green-400 focus:tw-ring-offset-2 tw-shadow-md hover:tw-shadow-lg tw-transition-all tw-duration-200 tw-flex tw-items-center"
            >
              <svg className="tw-w-4 tw-h-4 tw-mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {isEditing ? 'Update Member' : 'Add Member'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberFormModal;
